// Pure data-shaping and rendering for the Board page: the desktop kanban's
// columns laid out as a vertical list of collapsible sections. Each column maps
// to one lifecycle stage — the human gates (Approve, Decide, Accept, Audit)
// interleaved with the in-flight stages — and is filled from the same data
// sources the desktop board uses (../fabrika/web/src/views/board.ts). Cards are
// compact; tapping one opens its full-page detail (see detail.ts). No routing
// or connection logic lives here.
import { el } from "../dom.js";
import type { Attention, BigTask, Decision, Plan, ReviewItem, Task } from "../types.js";
import type { BoardCtx } from "./detail.js";
import {
  openAuditDetail,
  openBigTaskDetail,
  openDecideDetail,
  openPlanDetail,
  openReviewDetail,
  openTaskDetail,
} from "./detail.js";

type ColId =
  | "backlog"
  | "planning"
  | "approve"
  | "decide"
  | "ready"
  | "running"
  | "verifying"
  | "accept"
  | "audit"
  | "merged"
  | "closed";

// Columns left-to-right on the desktop become top-to-bottom here. `gate` marks
// the human-action columns ("needs you"); they carry a dot in the header.
const COLUMNS: { id: ColId; label: string; gate?: boolean }[] = [
  { id: "backlog", label: "Backlog" },
  { id: "planning", label: "Planning" },
  { id: "approve", label: "Approve", gate: true },
  { id: "decide", label: "Decide", gate: true },
  { id: "ready", label: "Ready" },
  { id: "running", label: "Running" },
  { id: "verifying", label: "Verifying" },
  { id: "accept", label: "Accept", gate: true },
  { id: "audit", label: "Audit", gate: true },
  { id: "merged", label: "Merged" },
  { id: "closed", label: "Closed" },
];

// In-flight task statuses share the Running column; pre-plan big-task statuses
// share Planning — same buckets the desktop board uses.
const IN_FLIGHT = ["claimed", "running"];
const PRE_PLAN = ["draft", "planning", "error"];

// Terminal columns collapse by default — reference, not the day's focus. Every
// other column opens when it holds work and collapses when empty.
const COLLAPSED_DEFAULT = new Set<ColId>(["merged", "closed"]);

interface Column {
  id: ColId;
  cards: HTMLElement[];
}

// Bucket every data source into its column, exactly as the desktop board's
// paint() does — including the audit/merged overlap (auto-merged tasks live in
// Audit, not Merged, until acknowledged).
function buildColumns(att: Attention, bigTasks: BigTask[], tasks: Task[], ctx: BoardCtx): Column[] {
  const plans = att.plans ?? [];
  const decisions = att.decisions ?? [];
  const reviews = att.reviews ?? [];
  const audits = att.audits ?? [];
  const auditIds = new Set(audits.map((a) => a.task.id));
  const byStatus = (s: string) => tasks.filter((t) => t.status === s);

  const cardsFor = (id: ColId): HTMLElement[] => {
    switch (id) {
      case "backlog":
        return bigTasks.filter((b) => b.status === "backlog").map((b) => bigTaskCard(b, ctx));
      case "planning":
        return bigTasks.filter((b) => PRE_PLAN.includes(b.status)).map((b) => bigTaskCard(b, ctx));
      case "approve":
        return plans.filter((p) => p.status === "proposed").map((p) => planCard(p, ctx));
      case "decide":
        return decisions.map((d) => decideCard(d, ctx));
      case "ready":
        return byStatus("ready").map((t) => taskCard(t, "Task", ctx));
      case "running":
        return tasks.filter((t) => IN_FLIGHT.includes(t.status)).map((t) => taskCard(t, "Task", ctx));
      case "verifying":
        return byStatus("verifying").map((t) => taskCard(t, "Task", ctx));
      case "accept":
        return reviews.map((r) => reviewCard(r, ctx));
      case "audit":
        return audits.map((r) => auditCard(r, ctx));
      case "merged":
        return byStatus("merged").filter((t) => !auditIds.has(t.id)).map((t) => taskCard(t, "Task", ctx));
      case "closed":
        return byStatus("closed").map((t) => taskCard(t, "Task", ctx));
    }
  };

  return COLUMNS.map((c) => ({ id: c.id, cards: cardsFor(c.id) }));
}

export function renderBoard(att: Attention, bigTasks: BigTask[], tasks: Task[], ctx: BoardCtx): HTMLElement {
  const container = el("div", { class: "work-groups" });
  const columns = buildColumns(att, bigTasks, tasks, ctx);
  for (const col of columns) {
    const meta = COLUMNS.find((c) => c.id === col.id)!;
    const open = col.cards.length > 0 && !COLLAPSED_DEFAULT.has(col.id);
    container.append(
      el("details", { class: `work-group${meta.gate ? " gate" : ""}`, open }, [
        el("summary", {}, [
          meta.gate ? el("span", { class: "gate-dot", title: "needs you" }) : "",
          meta.label,
          el("span", { class: "badge count" }, [String(col.cards.length)]),
        ]),
        ...(col.cards.length ? col.cards : [el("div", { class: "board-empty" }, ["empty"])]),
      ]),
    );
  }
  return container;
}

// ── Cards ──────────────────────────────────────────────────────────────────
// A compact, tappable row: a kind tag, the title, and a one-line meta. The tap
// opens the item's full-page detail — the same split the desktop board uses
// (small cards, rich modal).

function card(kind: string, kindLabel: string, title: string, meta: string, onClick: () => void): HTMLElement {
  return el("div", { class: `card board-card ${kind}`, role: "button", tabindex: 0, onclick: onClick }, [
    el("h3", {}, [el("span", { class: `kind-tag ${kind}` }, [kindLabel]), title]),
    meta ? el("p", { class: "meta" }, [meta]) : "",
  ]);
}

function bigTaskCard(b: BigTask, ctx: BoardCtx): HTMLElement {
  const meta = b.error || b.intent || b.status;
  return card("bigtask", "Plan", b.title, meta, () => openBigTaskDetail(b, ctx));
}

function planCard(p: Plan, ctx: BoardCtx): HTMLElement {
  const n = (p.tasks ?? []).length;
  return card("bigtask", "Plan", p.bigTask?.title ?? "Plan", `${n} task${n === 1 ? "" : "s"}`, () => openPlanDetail(p, ctx));
}

function decideCard(d: Decision, ctx: BoardCtx): HTMLElement {
  return card("task", "Decide", d.question, d.taskId ? "task" : "plan", () => openDecideDetail(d, ctx));
}

function reviewCard(it: ReviewItem, ctx: BoardCtx): HTMLElement {
  const t = it.task;
  const bits = [t.status, t.riskTier ? `${t.riskTier} risk` : ""].filter(Boolean).join(" · ");
  return card("task", "Review", t.title, bits, () => openReviewDetail(it, ctx));
}

function auditCard(it: ReviewItem, ctx: BoardCtx): HTMLElement {
  const t = it.task;
  const bits = ["auto-merged", t.riskTier ? `${t.riskTier} risk` : ""].filter(Boolean).join(" · ");
  return card("task", "Audit", t.title, bits, () => openAuditDetail(it, ctx));
}

function taskCard(t: Task, kindLabel: string, ctx: BoardCtx): HTMLElement {
  const bits = [t.riskTier ? `${t.riskTier} risk` : "", t.agentId ? `@${t.agentId}` : ""].filter(Boolean).join(" · ");
  return card("task", kindLabel, t.title, bits, () => openTaskDetail(t, ctx));
}
